import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// The desktop tool uses reportlab with bottomup=0 (origin top-left, y grows down).
// pdf-lib uses origin bottom-left, y grows up. We translate y values via PAGE_H - y.
// A4 in points: 595.28 x 841.89. Original code draws within the 10..580 x 10..835 box.
const PAGE_W = 595.28;
const PAGE_H = 841.89;

const HSN_CODE = '997315';

function flipY(y) { return PAGE_H - y; }

export async function renderChallanPdf({
  company,
  challanNo,
  dateStr,
  customerName,
  customerLines,
  customerGst,
  rows,
}) {
  const co = company || { name: '', address: '', contact: '', gstin: '' };
  const doc = await PDFDocument.create();
  const page = doc.addPage([PAGE_W, PAGE_H]);
  const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const timesBold = await doc.embedFont(StandardFonts.TimesRomanBold);
  const timesRoman = await doc.embedFont(StandardFonts.TimesRoman);

  const black = rgb(0, 0, 0);
  const white = rgb(1, 1, 1);

  // Helpers that take "y-from-top" inputs to keep parity with original code.
  function fillRect(x, yTop, w, h, color) {
    page.drawRectangle({
      x, y: PAGE_H - yTop - h, width: w, height: h,
      color,
    });
  }
  function line(x1, y1, x2, y2) {
    page.drawLine({
      start: { x: x1, y: flipY(y1) },
      end: { x: x2, y: flipY(y2) },
      thickness: 1.5,
      color: black,
    });
  }
  function text(str, x, yTop, font, size, color = black) {
    page.drawText(String(str ?? ''), {
      x, y: flipY(yTop) - size * 0.25, // approx baseline correction
      font, size, color,
    });
  }
  function textCentered(str, xCenter, yTop, font, size, color = black) {
    const s = String(str ?? '');
    const w = font.widthOfTextAtSize(s, size);
    text(s, xCenter - w / 2, yTop, font, size, color);
  }

  // --- Header banner (black filled rect with white text, all from company info) ---
  fillRect(10, 10, 570, 111, black);
  textCentered(co.name, 295, 42, helvBold, 30, white);
  textCentered(co.address, 295, 69, helvBold, 12, white);
  textCentered(co.contact, 295, 84, helvBold, 12, white);
  textCentered(co.gstin, 295, 111, helvBold, 18, white);

  // --- Outer frame ---
  line(10, 121, 580, 121);
  line(10, 10, 10, 835);
  line(580, 10, 580, 835);
  line(10, 10, 580, 10);
  line(10, 835, 580, 835);

  // --- Title ---
  textCentered('DELIVERY CHALLAN', 295, 169, helvBold, 18);

  // --- Challan no / date bars ---
  fillRect(10, 210, 169, 21, black);
  fillRect(480, 210, 100, 21, black);
  text('Challan No :', 12, 227, helvBold, 12, white);
  text(String(challanNo), 111, 227, helvBold, 12, white);
  text(`Date: ${dateStr}`, 483, 227, helvBold, 9, white);

  // --- Customer block ---
  text('Deliver To (Customer)', 12, 280, timesBold, 9);
  text(customerName, 12, 305, timesBold, 9);
  let yLine = 320;
  for (const ln of (customerLines || []).slice(0, 5)) {
    text(ln, 12, yLine, timesRoman, 9);
    yLine += 10;
  }
  text(customerGst || '', 12, 360, timesRoman, 9);

  // --- Items table frame ---
  // Outer rect (stroke only)
  page.drawRectangle({
    x: 10, y: PAGE_H - 380 - 390, width: 570, height: 390,
    borderColor: black, borderWidth: 1.5,
  });
  line(10, 395, 500, 395);
  line(10, 740, 580, 740);
  line(500, 380, 500, 770);
  line(10, 420, 580, 420);
  line(54, 395, 54, 740);
  line(95, 395, 95, 740);
  line(154, 395, 154, 740);
  line(180, 395, 180, 740);

  // Headers
  text('Details of rented equipment right to use, possession & effective control of which is transfered without ownership rights',
    12, 390, timesRoman, 6);
  text('HSN Code', 12, 414, timesRoman, 9);
  text('Number', 58, 414, timesRoman, 9);
  text('Serial Number', 96, 414, timesRoman, 9);
  text('Brand', 156, 414, timesRoman, 9);
  text('Details of rental laptops', 270, 414, timesRoman, 9);
  text('Quantity', 522, 402, timesRoman, 9);

  // --- Rows ---
  let total = 0;
  let rowY = 432;
  let prevDesc = '';
  for (const r of rows) {
    text(HSN_CODE, 12, rowY, timesRoman, 9);
    text(r.number, 58, rowY, timesRoman, 9);
    text(r.sr_no, 96, rowY, timesRoman, 9);
    text(r.brand, 156, rowY, timesRoman, 9);
    let desc = r.description || '';
    if (desc.trim() === '***') desc = prevDesc;
    text(desc, 185, rowY, timesRoman, 8);
    text(r.quantity, 531, rowY, timesRoman, 9);
    prevDesc = desc;
    const q = parseInt(r.quantity, 10);
    if (!Number.isNaN(q)) total += q;
    rowY += 15;
  }

  // --- Total --- box runs y=740..770; center text vertically in the band.
  text('Total', 442, 760, timesBold, 15);
  text(String(total), 560, 760, timesBold, 15);
  line(369, 740, 369, 770);

  // --- Footer ---
  text('Terms & Conditions: As mentioned in invoice/agreement', 12, 750, timesRoman, 6);
  text('All disputes shall be subject to Delhi jurisdiction', 12, 757, timesRoman, 6);
  text('(This is system generated challan)', 69, 830, timesRoman, 9);
  text('Authorised Signatory', 490, 830, timesRoman, 9);

  return doc.save();
}
